import { useEffect, useRef, useState } from 'react';
import { Smartphone, RefreshCw, Power, QrCode, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { crmApi } from '../crmApi';
import { UserSession } from '../../../types';

interface Props { session?: UserSession | null; }

const MeuWhatsapp = ({ session }: Props) => {
  const meuEmail = (session as any)?.usuario?.email || '';
  const [status, setStatus] = useState<any>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [iniciando, setIniciando] = useState(false);
  const intervalRef = useRef<any>(null);

  const carregar = async () => {
    setCarregando(true);
    try {
      const s = await crmApi.whatsapp.minhaSessao.status(session);
      setStatus(s);
      if (s.whatsapp?.conectado) {
        setQr(null);
      } else {
        const q = await crmApi.whatsapp.minhaSessao.qr(session);
        setQr(q.qr || q.qrCode || null);
      }
    } catch (e: any) { console.error(e); }
    finally { setCarregando(false); }
  };

  const conectar = async () => {
    setIniciando(true);
    try {
      await crmApi.whatsapp.minhaSessao.conectar(session);
      // Polling do QR e status
      let tentativas = 0;
      intervalRef.current = setInterval(async () => {
        tentativas += 1;
        await carregar();
        if (status?.whatsapp?.conectado || tentativas > 60) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }, 3000);
      await carregar();
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setIniciando(false); }
  };

  const desconectar = async () => {
    if (!confirm('Desconectar seu WhatsApp do CRM? Será necessário ler o QR Code novamente.')) return;
    try {
      await crmApi.whatsapp.minhaSessao.desconectar(session);
      setQr(null);
      await carregar();
    } catch (e: any) { alert('Erro: ' + e.message); }
  };

  useEffect(() => {
    carregar();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    // eslint-disable-next-line
  }, [session]);

  // Auto refresh enquanto não conectado
  useEffect(() => {
    if (status?.whatsapp?.conectado) return;
    const t = setInterval(carregar, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [status?.whatsapp?.conectado]);

  const conectado = !!status?.whatsapp?.conectado;
  const numero = status?.whatsapp?.numeroConfigurado;

  // QR pode vir como data URL, base64 puro (PNG) ou texto
  const qrIsDataUrl = !!(qr && qr.startsWith('data:image'));
  const qrIsBase64Png = !!(qr && !qrIsDataUrl && /^[A-Za-z0-9+/=]+$/.test(qr) && qr.length > 200);
  const qrSrc = qrIsDataUrl ? qr! : qrIsBase64Png ? `data:image/png;base64,${qr}` : null;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className={`rounded-xl border p-5 ${conectado ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-lg ${conectado ? 'bg-green-600' : 'bg-amber-500'}`}>
            <Smartphone className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              Meu WhatsApp pessoal
              {conectado ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <XCircle className="h-4 w-4 text-amber-600" />
              )}
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">
              Sessão: <code className="bg-white px-1.5 py-0.5 rounded">{meuEmail}</code>
            </p>
            {conectado && numero && (
              <p className="text-xs text-green-700 mt-1">📱 Conectado: +{numero}</p>
            )}
          </div>
          <button onClick={carregar} title="Atualizar"
            className="p-2 hover:bg-white rounded text-gray-500">
            <RefreshCw className={`h-4 w-4 ${carregando ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
        <p className="font-semibold mb-1">ℹ️ Importante</p>
        <p>Este é o WhatsApp <b>do vendedor</b> (independente do WhatsApp da empresa que está em <i>Fluxos</i>). Cada usuário conecta seu próprio número aqui — todas as mensagens enviadas pelo CRM (drawer dos cards, Inbox, Disparo) sairão pelo seu aparelho.</p>
      </div>

      {!conectado ? (
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
          {qr ? (
            <>
              <QrCode className="h-6 w-6 mx-auto text-gray-400 mb-2" />
              <p className="text-sm font-semibold text-gray-800 mb-3">Escaneie o QR Code com seu WhatsApp</p>
              <p className="text-xs text-gray-500 mb-4">No celular: Mais opções → Aparelhos conectados → Conectar aparelho</p>
              <div className="flex justify-center bg-white p-3 rounded-lg border border-gray-200 max-w-xs mx-auto">
                {qrSrc ? (
                  <img src={qrSrc} alt="QR Code" className="max-w-full" />
                ) : (
                  <pre className="text-[6px] leading-[6px] font-mono text-black select-none whitespace-pre">{qr}</pre>
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-3">Atualizando automaticamente a cada 5s…</p>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                {iniciando ? 'Inicializando sessão…' : 'Sua sessão ainda não foi iniciada.'}
              </p>
              <button onClick={conectar} disabled={iniciando}
                className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-md hover:bg-green-700 disabled:opacity-40 inline-flex items-center gap-2">
                {iniciando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
                {iniciando ? 'Iniciando...' : 'Conectar WhatsApp'}
              </button>
              <p className="text-[11px] text-gray-500 mt-3">Após clicar, aguarde alguns segundos para o QR aparecer.</p>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
          <CheckCircle2 className="h-10 w-10 mx-auto text-green-600 mb-2" />
          <p className="text-sm font-semibold text-gray-800 mb-1">WhatsApp conectado</p>
          <p className="text-xs text-gray-500 mb-4">Você já pode enviar e receber mensagens pelo CRM.</p>
          <button onClick={desconectar}
            className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 text-sm font-medium rounded-md hover:bg-red-100 inline-flex items-center gap-2">
            <Power className="h-4 w-4" /> Desconectar
          </button>
        </div>
      )}
    </div>
  );
};

export default MeuWhatsapp;
